import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLearnArticles } from "@/hooks/learn/useLearnArticles";
import { cn } from "@/lib/utils";
import ArticleCard from "./ArticleCard";

const ArticleLibrary = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState<string | undefined>(undefined);

  const { data, isLoading } = useLearnArticles({
    searchTerm: searchTerm.trim() || undefined,
    category,
  });

  const articles = data?.articles ?? [];

  const categories = useMemo(() => {
    const set = new Set<string>();
    articles.forEach((a) => a.category && set.add(a.category));
    return Array.from(set).sort();
  }, [articles]);

  return (
    <div>
      {/* Search + filters row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <Input
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 bg-white border-gray-200 focus-visible:ring-genie-primary"
          />
        </div>
      </div>

      {(categories.length > 0 || category) && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          <button
            onClick={() => setCategory(undefined)}
            className={cn(
              "px-2.5 py-1 rounded-full text-xs font-medium transition-all capitalize",
              !category
                ? "bg-gradient-to-r from-genie-primary to-genie-secondary text-white shadow-sm"
                : "bg-white border border-gray-200 text-gray-600 hover:border-genie-primary/40 hover:text-genie-primary",
            )}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat === category ? undefined : cat)}
              className={cn(
                "px-2.5 py-1 rounded-full text-xs font-medium transition-all capitalize",
                category === cat
                  ? "bg-gradient-to-r from-genie-primary to-genie-secondary text-white shadow-sm"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-genie-primary/40 hover:text-genie-primary",
              )}
            >
              {cat.replace(/-/g, " ")}
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-40 rounded-xl bg-white border border-gray-200 animate-pulse" />
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 bg-white p-8 text-center">
          <p className="text-sm text-gray-600">No articles match your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {articles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ArticleLibrary;
